/** Pack/flavor selection for Quellvo. Vessel's native component handles cart writes. */
export function resolveVariant(variants, choices) {
  return variants.find((variant) => variant.options.length === choices.length &&
    variant.options.every((value, index) => value === choices[index]));
}

export function initializePurchase(root) {
  if (root.dataset.qvInitialized || !root.querySelector('[data-qv-variant]') || !root.querySelector('[data-qv-add]')) return;
  const variants = JSON.parse(root.querySelector('[data-qv-variants]').textContent);
  const labels = JSON.parse(root.querySelector('[data-qv-labels]').textContent);
  const controller = new window.AbortController();
  const select = root.querySelector('[data-qv-variant]');
  const groups = [...root.querySelectorAll('[data-qv-option]')];
  const addButton = root.querySelector('[data-qv-add]');
  const selectedChoices = () => groups.map((group) => group.querySelector('input:checked')?.value);
  const render = (updateUrl = true) => {
    const choices = selectedChoices();
    const variant = resolveVariant(variants, choices);
    select.value = variant ? String(variant.id) : '';
    addButton.disabled = !variant?.available;
    root.querySelector('[data-qv-add-label]').textContent = !variant ? labels.unavailable : variant.available ? labels.add : labels.sold;
    root.querySelector('[data-qv-total]').textContent = variant?.money || 'Unavailable';
    root.querySelector('[data-qv-summary]').textContent = variant?.title || 'This combination is unavailable.';
    root.querySelector('[data-qv-sticky-price]').textContent = variant?.money || '';
    groups.forEach((group, index) => {
      root.querySelector(`[data-qv-option-label="${index}"]`).textContent = choices[index] || '';
      group.querySelectorAll('input[type="radio"]').forEach((radio) => {
        const candidateChoices = [...choices];
        candidateChoices[index] = radio.value;
        const candidate = resolveVariant(variants, candidateChoices);
        const card = radio.closest('.qv-option-card');
        card.dataset.unavailable = String(!candidate?.available);
        const price = card.querySelector('[data-qv-pack-price]');
        if (price) price.textContent = candidate?.money || 'Unavailable';
      });
    });
    if (updateUrl && variant) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', String(variant.id));
      window.history.replaceState(window.history.state, '', url);
    }
    // Replace stale feedback when a shopper chooses another combination.
    root.querySelector('[ref="addToCartTextError"]')?.classList.add('hidden');
  };
  groups.forEach((group) => group.addEventListener('change', () => render()));
  const useNativeSelection = () => {
    const variant = variants.find((candidate) => String(candidate.id) === select.value);
    if (variant) groups.forEach((group, index) => {
      group.querySelectorAll('input').forEach((radio) => { radio.checked = radio.value === variant.options[index]; });
    });
    render(false);
  };
  select.addEventListener('change', useNativeSelection);
  const chooseOption = (value) => {
    const radio = groups.flatMap((group) => [...group.querySelectorAll('input')]).find((input) => input.value === value);
    if (radio) { radio.checked = true; render(); }
  };
  // Shortcut links outside the purchase section retain the current other option.
  const shortcuts = (event) => {
    const link = event.target.closest('[data-qv-select-flavor], [data-qv-select-pack]');
    if (link) chooseOption(link.dataset.qvSelectFlavor || link.dataset.qvSelectPack);
  };
  document.addEventListener('click', shortcuts, { signal: controller.signal });
  window.addEventListener('popstate', () => {
    const id = new URL(window.location.href).searchParams.get('variant');
    const variant = variants.find((candidate) => String(candidate.id) === id);
    if (variant) { select.value = String(variant.id); useNativeSelection(); }
  }, { signal: controller.signal });
  useNativeSelection();
  root.querySelector('.qv-native-variant').hidden = true;
  root.querySelector('.qv-enhanced-options').hidden = false;
  root.dataset.qvInitialized = 'true';
  const sticky = root.querySelector('[data-qv-sticky]');
  let observer;
  if ('IntersectionObserver' in window) {
    observer = new window.IntersectionObserver(([entry]) => {
      sticky.hidden = entry.isIntersecting || entry.boundingClientRect.top > 0;
    }, { rootMargin: '-100px 0px 0px 0px' });
    observer.observe(root.querySelector('form'));
  }
  root.qvCleanup = () => { controller.abort(); observer?.disconnect(); };
}

export function initializeGallery(gallery) {
  if (gallery.dataset.qvInitialized) return;
  const buttons = [...gallery.querySelectorAll('[data-qv-thumb]')];
  const panels = [...gallery.querySelectorAll('[data-qv-panel]')];
  const select = (button) => {
    buttons.forEach((item) => item.setAttribute('aria-pressed', String(item === button)));
    panels.forEach((panel) => { panel.hidden = panel.dataset.qvPanel !== button.dataset.qvThumb; });
  };
  buttons.forEach((button, index) => {
    button.addEventListener('click', () => select(button));
    button.addEventListener('keydown', (event) => {
      if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      event.preventDefault();
      const next = buttons[(index + (event.key === 'ArrowRight' ? 1 : -1) + buttons.length) % buttons.length];
      next.focus(); select(next);
    });
  });
  gallery.dataset.qvInitialized = 'true';
}

function initialize(scope = document) {
  scope.querySelectorAll('[data-qv-purchase]').forEach(initializePurchase);
  scope.querySelectorAll('[data-qv-gallery]').forEach(initializeGallery);
}
if (typeof document !== 'undefined') {
  initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-qv-purchase]').forEach((root) => root.qvCleanup?.());
  });
}
